import { CompetencyBadge } from '@/components/feedback/CompetencyBadge'
import { scoreBarColor, scoreColor } from '@/lib/scoreColors'
import { cn } from '@/lib/utils'
import type { Difficulty, StoryBankEntry, StorySource } from '@/types/interview'
import { Star, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

// A single click arms the delete button; a second click within this window
// confirms permanent deletion. Guards against the accidental single-click
// permanent delete a plain onClick handler would allow.
const DELETE_CONFIRM_TIMEOUT_MS = 3000

const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  entry: 'Entry',
  mid: 'Mid',
  senior: 'Senior',
}

const SOURCE_LABELS: Record<StorySource, string | null> = {
  session_answer: null, // the default case needs no badge
  mined: 'AI-mined',
  manual: 'Manual',
}

const STAR_BARS: { key: keyof NonNullable<StoryBankEntry['scores']>; label: string }[] = [
  { key: 'situation', label: 'S' },
  { key: 'task', label: 'T' },
  { key: 'action', label: 'A' },
  { key: 'result', label: 'R' },
]

interface StoryCardProps {
  story: StoryBankEntry
  onOpen: (storyId: string) => void
  onDelete: (storyId: string) => void
  isDeleting?: boolean
}

export function StoryCard({ story, onOpen, onDelete, isDeleting }: StoryCardProps) {
  // Preview line: the question for session answers, the situation for standalone stories
  const previewSource = story.questionText ?? story.star?.situation ?? ''
  const preview = previewSource.length > 120 ? `${previewSource.slice(0, 120)}…` : previewSource
  const sourceLabel = SOURCE_LABELS[story.source]

  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    if (!confirmingDelete) return
    const timer = setTimeout(() => setConfirmingDelete(false), DELETE_CONFIRM_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [confirmingDelete])

  return (
    <div
      className={cn(
        'relative rounded-xl border border-border bg-background p-4 space-y-3 transition-all',
        'cursor-pointer hover:border-primary/40 hover:shadow-sm',
        isDeleting && 'opacity-40 pointer-events-none'
      )}
      onClick={() => onOpen(story.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(story.id)
        }
      }}
      // biome-ignore lint/a11y/useSemanticElements: a native <button> cannot contain the nested delete <button> (invalid HTML) — div with role="button" + keyboard handling is the standard pattern
      role="button"
      tabIndex={0}
      aria-label={`Open story: ${story.title}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug flex items-center gap-1.5">
            {story.isFavorite && (
              <Star
                className="h-3.5 w-3.5 flex-shrink-0 fill-amber-400 text-amber-400"
                aria-label="Favorited"
              />
            )}
            <span className="truncate">{story.title}</span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {story.competency && <CompetencyBadge competency={story.competency} />}
            {sourceLabel && (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {sourceLabel}
              </span>
            )}
            {story.difficulty && story.targetRole && (
              <span className="text-xs text-muted-foreground">
                {DIFFICULTY_LABELS[story.difficulty]} · {story.targetRole}
              </span>
            )}
            {!story.targetRole && story.roleTag && (
              <span className="text-xs text-muted-foreground">{story.roleTag}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            if (confirmingDelete) {
              onDelete(story.id)
            } else {
              setConfirmingDelete(true)
            }
          }}
          onBlur={() => setConfirmingDelete(false)}
          className={cn(
            'flex-shrink-0 rounded-md px-1.5 py-1.5 text-xs font-medium transition-colors',
            confirmingDelete
              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
          )}
          aria-label={
            confirmingDelete ? 'Click again to permanently delete' : 'Remove from Story Bank'
          }
        >
          {confirmingDelete ? 'Confirm?' : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Preview */}
      {preview && <p className="text-xs text-muted-foreground leading-relaxed italic">{preview}</p>}

      {/* STAR mini-bars (session answers only) */}
      {story.scores && (
        <div className="flex gap-3">
          {STAR_BARS.map(({ key, label }) => {
            const scores = story.scores
            if (!scores) return null
            const val = scores[key]
            const pct = ((val - 1) / 4) * 100
            return (
              <div key={key} className="flex-1 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-mono text-muted-foreground">{label}</span>
                  <span className={cn('text-[10px] font-semibold tabular-nums', scoreColor(val))}>
                    {val.toFixed(1)}
                  </span>
                </div>
                <div className="h-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn('h-full rounded-full', scoreBarColor(val))}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Date */}
      <p className="text-[10px] text-muted-foreground">
        Saved{' '}
        {new Date(story.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </p>
    </div>
  )
}
